import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useLayoutEffect,
  useCallback,
} from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  TrendingUp,
  TrendingDown,
  Minus,
  FileUp,
  Search,
  RefreshCw,
  AlertCircle,
  Link as LinkIcon,
  Table as TableIcon,
  ChevronRight,
  ArrowLeft,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Loader2,
  Upload,
  Download,
  Save,
  Trash2,
  Edit2,
  Plus,
  LayoutList,
  LayoutGrid,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  LogIn,
  Pin,
  GripVertical,
  Newspaper,
  Users,
  LineChart,
  Activity,
  Linkedin,
  Facebook,
  Instagram,
} from "lucide-react";
import { PortfolioItem, AnalysisResults } from "./types";
import { TextWithTooltips, TooltipSpan } from "./components/TooltipSpan";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  useDroppable,
  DragOverEvent,
  DragStartEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { SortableRow, SortableMobileRow } from "./components/SortableRow";
import {
  MobileRowComponent,
  DesktopRowComponent,
  PerfRowComponent,
  PortfolioPerfRowComponent,
} from "./components/RowComponents";
import { SEO } from "./components/SEO";
import { useModals } from "./components/UIModals";
import { Logo } from "./components/Logo";
import type { SortColumn, SortDirection, LiveQuoteData, TradeDuration } from "./types";
import { auth } from "./lib/firebase";
import {
  signInWithPopup,
  signInWithRedirect,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
  linkWithCredential,
  signInWithCredential,
} from "firebase/auth";

import { Capacitor } from "@capacitor/core";
import type { DbPortfolio, DbUser } from "./lib/db";

const getDbService = async () => {
  const mod = await import("./lib/db");
  return mod.dbService;
};
import { useTranslation } from "react-i18next";
import {
  getAnalysisFromCache,
  saveAnalysisToCache,
  clearAnalysisCache,
} from "./lib/cache";
import { formatCurrency, formatPerf, resolveThemeForItem, normalizeTicker } from "./lib/utils";
import { checkIsPortfolioDirty } from "./lib/portfolioUtils";
import { getPerfValue, PERF_OPTIONS } from "./components/RowComponents";
import { apiFetch } from "./lib/apiFetch";
import { IndicatorRow, getIndicatorTooltipText } from "./components/IndicatorRow";
import { TickerEditor } from "./components/TickerEditor";
import { ThemeInput } from "./components/ThemeInput";
import { SortableCategoryHeader } from "./components/SortableCategoryHeader";
import { AppHeader } from "./components/AppHeader";
import { TopPickBanner } from "./components/TopPickBanner";
import { AndroidInstallBanner } from "./components/AndroidInstallBanner";
import { IosInstallBanner } from "./components/IosInstallBanner";
import { PortfolioPanel } from "./features/portfolio/components/PortfolioPanel";
import { NewsTicker } from "./components/NewsTicker";

const AnalysisPanel = React.lazy(() =>
  import("./features/analysis/components/AnalysisPanel").then((module) => ({
    default: module.AnalysisPanel,
  }))
);

const useEventCallback = <T extends (...args: any[]) => any>(handler: T): T => {
  const handlerRef = useRef(handler);
  useLayoutEffect(() => {
    handlerRef.current = handler;
  });
  return useCallback((...args: Parameters<T>) => {
    const fn = handlerRef.current;
    return fn(...args);
  }, []) as T;
}


export const DEFAULT_EXAMPLE_PORTFOLIO: PortfolioItem[] = [
  { id: "example-aapl", ticker: "AAPL", allocation: "10" },
  { id: "example-msft", ticker: "MSFT", allocation: "20" },
  { id: "example-googl", ticker: "GOOGL", allocation: "20" },
  { id: "example-amzn", ticker: "AMZN", allocation: "10" },
  { id: "example-meta", ticker: "META", allocation: "5" },
  { id: "example-mu", ticker: "MU", allocation: "10" },
  { id: "example-exel", ticker: "EXEL", allocation: "5" },
  { id: "example-adbe", ticker: "ADBE", allocation: "5" },
  { id: "example-nvda", ticker: "NVDA", allocation: "5" },
  { id: "example-shel", ticker: "SHEL", allocation: "5" },
];

const hasStoredAuth = (): boolean => {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith("firebase:authUser:")) {
        return true;
      }
    }
  } catch { }
  return false;
};

export default function App() {
  const { t } = useTranslation();
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>(() => {
    return hasStoredAuth() ? [] : DEFAULT_EXAMPLE_PORTFOLIO;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTicker, setSelectedTicker] = useState<string | null>(() => {
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/analysis/")) {
      const segs = window.location.pathname.split("/");
      return segs[2] ? normalizeTicker(segs[2].toUpperCase()) : null;
    }
    return null;
  });
  const [activeAnalysis, setActiveAnalysis] = useState<AnalysisResults | null>(null);
  const [isChartMaximized, setIsChartMaximized] = useState(false);
  const [activeThemeId, setActiveThemeId] = useState<string | null>(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ loaded: 0, total: 0 });
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const [sortColumn, setSortColumn] = useState<SortColumn>("default");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [viewMode, setViewMode] = useState<"LIST" | "THEME" | "PERF">("LIST");
  const [showTechFundDetails, setShowTechFundDetails] = useState(false);
  const [listPerfMetric, setListPerfMetric] = useState<string>("1D");
  const [maxRiskPerTradePct, setMaxRiskPerTradePct] = useState<number>(() => {
    try {
      const saved = localStorage.getItem("maxRiskPerTradePct");
      return saved ? parseFloat(saved) : 0.01;
    } catch {
      return 0.01;
    }
  });
  const [tradeDuration, setTradeDuration] = useState<TradeDuration>(() => {
    try {
      const saved = localStorage.getItem("tradeDuration") as TradeDuration;
      return ['short_swing', 'position', 'long_term'].includes(saved) ? saved : 'position';
    } catch {
      return 'position';
    }
  });
  const [maxAllocationCapPct, setMaxAllocationCapPct] = useState<number>(() => {
    try {
      const saved = localStorage.getItem("maxAllocationCapPct");
      return saved ? parseFloat(saved) : 0.15;
    } catch {
      return 0.15;
    }
  });

  const [liveQuotes, setLiveQuotes] = useState<
    Record<string, LiveQuoteData>
  >({});
  const [lastQuotesFetch, setLastQuotesFetch] = useState<number>(0);

  useEffect(() => {
    const tickersToFetch = portfolio.map((p) => p.ticker).filter((t) => !!t);

    if (tickersToFetch.length > 0) {
      apiFetch("/api/live-quotes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tickers: tickersToFetch }),
      })
        .then((res) => res.json())
        .then((data) => {
          setLiveQuotes(data);
        })
        .catch(console.error);
    }
  }, [portfolio.map((p) => p.ticker).join(","), lastQuotesFetch]);

  const [isProd, setIsProd] = useState(false);



  const [isExample, setIsExample] = useState<boolean>(() => !hasStoredAuth());
  const [portfoliosList, setPortfoliosList] = useState<DbPortfolio[]>([]);
  const [currentPortfolioId, setCurrentPortfolioId] = useState<string | null>(
    null,
  );
  const currentPortfolioIdRef = useRef(currentPortfolioId);
  const updateCurrentPortfolioId = (id: string | null) => {
    setCurrentPortfolioId(id);
    currentPortfolioIdRef.current = id;
  };
  useEffect(() => {
    currentPortfolioIdRef.current = currentPortfolioId;
  }, [currentPortfolioId]);
  const [defaultPortfolioId, setDefaultPortfolioId] = useState<string | null>(
    null,
  );
  const [currentPortfolioName, setCurrentPortfolioName] = useState<string>(() =>
    hasStoredAuth() ? t("portfolio.my_portfolio_default") : t("portfolio.example_portfolio_name", { defaultValue: "Example Portfolio" })
  );
  const { promptUser, promptPortfolio, confirmUser, alertUser, confirmUnsavedChanges, ModalComponents } = useModals();
  const [user, setUser] = useState<any>(null);
  const [customThemes, setCustomThemes] = useState<string[]>([]);

  const [topPickInfo, setTopPickInfo] = useState<{
    ticker: string;
    price: number;
    currency?: string;
  } | null>(() => {
    try {
      const cached = localStorage.getItem("topPickCacheLight");
      if (cached) {
        const parsed = JSON.parse(cached);
        const isFresh = Date.now() - (parsed.timestamp || 0) < 6 * 60 * 60 * 1000;
        if (isFresh && parsed.info) {
          return parsed.info;
        }
      }
    } catch (e) { }
    return null;
  });
  const [topPickLoading, setTopPickLoading] = useState<boolean>(() => {
    try {
      const cached = localStorage.getItem("topPickCacheLight");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Date.now() - (parsed.timestamp || 0) < 6 * 60 * 60 * 1000 && parsed.info) {
          return false;
        }
      }
    } catch (e) { }
    return true;
  });
  const [hideTopPickBanner, setHideTopPickBanner] = useState(false);

  useEffect(() => {
    let active = true;
    async function fetchTopPick() {
      if (hideTopPickBanner) {
        if (active) setTopPickLoading(false);
        return;
      }
      let fallbackInfo: any = null;
      try {
        const todayGMT = new Date().toISOString().split("T")[0];
        const cacheKey = "topPickCacheLight";

        // Clean up legacy cache keys from previous versions
        const legacyKeys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("topPickCacheLight_")) {
            legacyKeys.push(key);
          }
        }
        legacyKeys.forEach((k) => localStorage.removeItem(k));

        const cached = localStorage.getItem(cacheKey);
        let fallbackInfo: any = null;
        if (cached) {
          let fallbackInfo: any = null;
      try {
            const parsed = JSON.parse(cached);
            if (parsed.info) fallbackInfo = parsed.info;
            const isFresh = Date.now() - (parsed.timestamp || 0) < 6 * 60 * 60 * 1000; // 6 hours TTL
            if (isFresh && parsed.info) {
              if (active) {
                setTopPickInfo(parsed.info);
                setTopPickLoading(false);
              }
              return;
            }
          } catch (e) { }
        }

        const res = await apiFetch("/api/top-pick");
        const data = await res.json();
        const topPick = (data && data.topPicks && Array.isArray(data.topPicks) && data.topPicks[0]) || (data && data.topPick);
        if (topPick && topPick.ticker && active) {
          const info = {
            ticker: topPick.ticker,
            price: topPick.price || 0,
            currency: topPick.currency,
          };
          setTopPickInfo((prev) => {
            if (
              prev &&
              prev.ticker === info.ticker &&
              prev.price === info.price
            ) {
              return prev;
            }
            return info;
          });
          localStorage.setItem(
            cacheKey,
            JSON.stringify({
              info,
              timestamp: Date.now(),
            }),
          );
        } else if (fallbackInfo && active) {
          setTopPickInfo(fallbackInfo);
        }
      } catch (e) {
        if (fallbackInfo && active) {
          setTopPickInfo(fallbackInfo);
        }
      } finally {
        if (active) setTopPickLoading(false);
      }
    }
    fetchTopPick();
    return () => {
      active = false;
    };
  }, [hideTopPickBanner]);

  const isDirty = useMemo(() => {
    return checkIsPortfolioDirty({
      portfolio,
      currentPortfolioId,
      portfoliosList,
      customThemes,
      isExample,
      viewMode,
      showTechFundDetails,
      maxRiskPerTradePct,
      maxAllocationCapPct,
      tradeDuration,
    });
  }, [portfolio, currentPortfolioId, portfoliosList, customThemes, isExample, viewMode, showTechFundDetails, maxRiskPerTradePct, maxAllocationCapPct, tradeDuration]);

  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [analysisTab, setAnalysisTab] = useState<"Analysis" | "Profile" | "Valuation" | "News" | "Insights" | "Chart & Tech">("Analysis");
  const [profileData, setProfileData] = useState<any | null>(null);
  const [newsData, setNewsData] = useState<any[] | null>(null);
  const [eventsData, setEventsData] = useState<any | null>(null);
  const [valuationData, setValuationData] = useState<any | null>(null);
  const [analystData, setAnalystData] = useState<any | null>(null);
  const [insightsData, setInsightsData] = useState<any | null>(null);
  const [sentimentData, setSentimentData] = useState<any | null>(null);
  const [isExecSummaryExpanded, setIsExecSummaryExpanded] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    if (selectedTicker) {
      setAnalysisTab("Analysis");
      setIsExecSummaryExpanded(false);
      setNewsData(null);
      setValuationData(null);
      setAnalystData(null);
      setInsightsData(null);
      setSentimentData(null);
    }
  }, [selectedTicker]);

  useEffect(() => {
    let active = true;
    if (selectedTicker && analysisTab !== "Analysis") {
      setTabLoading(true);
      if (analysisTab === "News") {
        Promise.all([
          apiFetch(`/api/news?ticker=${selectedTicker}`).then((res) => res.json()),
          apiFetch(`/api/events?ticker=${selectedTicker}`).then((res) => res.json())
        ])
          .then(([news, events]) => {
            if (!active) return;
            setNewsData(news);
            setEventsData(events);
          })
          .catch(() => {
            if (!active) return;
            setNewsData([]);
            setEventsData({});
          })
          .finally(() => { if (active) setTabLoading(false); });
      } else if (analysisTab === "Valuation") {
        apiFetch(`/api/valuation?ticker=${selectedTicker}&t=${Date.now()}`)
          .then((res) => res.json())
          .then((data) => {
            if (!active) return;
            setValuationData(data);
          })
          .catch(() => {
            if (!active) return;
            setValuationData({});
          })
          .finally(() => { if (active) setTabLoading(false); });
      } else if (analysisTab === "Profile") {
        Promise.all([
          apiFetch(`/api/profile?ticker=${selectedTicker}`).then((res) => res.json()),
          apiFetch(`/api/sentiment?ticker=${selectedTicker}`).then((res) => res.json())
        ])
          .then(([profile, sentiment]) => {
            if (!active) return;
            setProfileData(profile);
            setSentimentData(sentiment);
          })
          .catch(() => {
            if (!active) return;
            setProfileData({});
            setSentimentData({});
          })
          .finally(() => { if (active) setTabLoading(false); });
      } else if (analysisTab === "Insights") {
        Promise.all([
          apiFetch(`/api/insights?ticker=${selectedTicker}`).then((res) => res.json()),
          apiFetch(`/api/sentiment?ticker=${selectedTicker}`).then((res) => res.json())
        ])
          .then(([insights, sentiment]) => {
            if (!active) return;
            setInsightsData(insights);
            setSentimentData(sentiment);
          })
          .catch(() => {
            if (!active) return;
            setInsightsData({});
            setSentimentData({});
          })
          .finally(() => { if (active) setTabLoading(false); });
      } else if (analysisTab === "Chart & Tech") {
        // Only insights: the tab renders the third-party Technical Sentiment cards from it. The
        // bars come from TechChart's own fetch, keyed by the chosen interval, and the engine's
        // technical stack comes from `currentAnalysis`. The duplicate /api/chart call that used
        // to sit here wrote to state nothing ever read.
        apiFetch(`/api/insights?ticker=${selectedTicker}`)
          .then((res) => res.json())
          .then((insights) => {
            if (!active) return;
            setInsightsData(insights);
          })
          .catch(() => {
            if (!active) return;
            setInsightsData({});
          })
          .finally(() => { if (active) setTabLoading(false); });
      }
    }
    return () => { active = false; };
  }, [selectedTicker, analysisTab, refreshTrigger]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 0, tolerance: 0 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDragId(event.active.id.toString());
    document.body.style.userSelect = "none";
    document.body.style.webkitUserSelect = "none";
  };

  const handleDragCancel = () => {
    setActiveDragId(null);
    document.body.style.userSelect = "";
    document.body.style.webkitUserSelect = "";
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    document.body.style.userSelect = "";
    document.body.style.webkitUserSelect = "";
    const { active, over } = event;
    if (!over) return;

    if (
      active.data.current?.type === "category" &&
      over.data.current?.type === "category"
    ) {
      const activeTheme = active.id.toString().replace("category-", "");
      const overTheme = over.id.toString().replace("category-", "");

      if (activeTheme !== overTheme) {
        setCustomThemes((prev) => {
          const groups: Record<string, PortfolioItem[]> = {};
          prev.forEach((t) => (groups[t] = []));
          portfolio.forEach((item) => {
            const theme = resolveThemeForItem(item, prev, Object.keys(groups), t("common.other"));
            if (!groups[theme]) groups[theme] = [];
            groups[theme].push(item);
          });
          const existingThemes = Object.keys(groups);
          let ordered = prev.filter((theme) =>
            existingThemes.includes(theme),
          );
          const nonCustom = existingThemes.filter(theme => !ordered.includes(theme)).sort();
          ordered.push(...nonCustom);
          ordered = Array.from(new Set(ordered));
          const oldIndex = ordered.indexOf(activeTheme);
          const newIndex = ordered.indexOf(overTheme);
          return arrayMove(ordered, oldIndex, newIndex);
        });
      }
      return;
    }

    if (
      active.data.current?.type !== "category" &&
      over.id.toString().startsWith("category-")
    ) {
      const targetTheme = over.id.toString().replace("category-", "");
      const activeIdClean = active.id.toString().replace("mob-", "").replace("desk-", "");
      setPortfolio((items) => {
        return items.map((i) =>
          i.id === activeIdClean ? { ...i, theme: targetTheme } : i,
        );
      });
      setCustomThemes((prev) =>
        prev.includes(targetTheme) ? prev : [...prev, targetTheme],
      );
      return;
    }

    if (active.id !== over.id) {
      const activeIdClean = active.id.toString().replace("mob-", "").replace("desk-", "");
      const overIdClean = over.id.toString().replace("mob-", "").replace("desk-", "");

      setPortfolio((items) => {
        const oldIndex = items.findIndex((i) => i.id === activeIdClean);
        const newIndex = items.findIndex((i) => i.id === overIdClean);

        if (oldIndex === -1 || newIndex === -1) return items;

        let newItems = [...items];

        if (viewMode === "THEME") {
          const targetThemeResolved = resolveThemeForItem(
            items[newIndex],
            customThemes,
            [],
            t("common.other")
          );
          if (newItems[oldIndex].theme !== targetThemeResolved) {
            newItems[oldIndex] = { ...newItems[oldIndex], theme: targetThemeResolved };
            setCustomThemes((prev) =>
              prev.includes(targetThemeResolved) ? prev : [...prev, targetThemeResolved],
            );
          }
        } else {
          const targetTheme = items[newIndex].theme;
          if (targetTheme && newItems[oldIndex].theme !== targetTheme) {
            newItems[oldIndex] = { ...newItems[oldIndex], theme: targetTheme };
          }
        }

        return arrayMove(newItems, oldIndex, newIndex);
      });
      setSortColumn("default"); // resetting sort due to custom drag ordering
    }
  };

  const handleMoveMobile = useEventCallback(
    (id: string, direction: "up" | "down") => {
      setPortfolio((prev) => {
        if (viewMode === "LIST") {
          let currentViewList = sortedPortfolio;
          const viewIdx = currentViewList.findIndex((i) => i.id === id);
          if (viewIdx < 0) return prev;

          const swapViewIdx = direction === "up" ? viewIdx - 1 : viewIdx + 1;
          if (swapViewIdx < 0 || swapViewIdx >= currentViewList.length)
            return prev;

          const swapId = currentViewList[swapViewIdx].id;

          const oldIndex = prev.findIndex((i) => i.id === id);
          const newIndex = prev.findIndex((i) => i.id === swapId);

          let newItems = [...prev];
          return arrayMove(newItems, oldIndex, newIndex);
        } else {
          const groups: Record<string, PortfolioItem[]> = {};
          customThemes.forEach((t) => (groups[t] = []));
          prev.forEach((item) => {
            const theme = resolveThemeForItem(item, customThemes, Object.keys(groups), t("common.other"));
            if (!groups[theme]) groups[theme] = [];
            groups[theme].push(item);
          });

          const existingThemes = Object.keys(groups);
          let ordered = customThemes.filter((t) =>
            existingThemes.includes(t),
          );
          const nonCustom = existingThemes.filter(t => !ordered.includes(t)).sort();
          ordered.push(...nonCustom);
          ordered = Array.from(new Set(ordered));

          const currentItem = prev.find((i) => i.id === id);
          if (!currentItem) return prev;

          const currentTheme = resolveThemeForItem(currentItem, customThemes, ordered, t("common.other"));
          const themeIdx = ordered.indexOf(currentTheme);
          if (themeIdx < 0) return prev;

          const itemsInCurrentTheme = groups[currentTheme] || [];
          const itemIdxInTheme = itemsInCurrentTheme.findIndex((i) => i.id === id);
          if (itemIdxInTheme < 0) return prev;

          if (direction === "up") {
            if (itemIdxInTheme > 0) {
              const swapItem = itemsInCurrentTheme[itemIdxInTheme - 1];
              const oldIndex = prev.findIndex((i) => i.id === id);
              const newIndex = prev.findIndex((i) => i.id === swapItem.id);
              return arrayMove([...prev], oldIndex, newIndex);
            } else if (themeIdx > 0) {
              const targetTheme = ordered[themeIdx - 1];
              const targetThemeItems = groups[targetTheme] || [];
              const oldIndex = prev.findIndex((i) => i.id === id);

              let updatedItem = { ...prev[oldIndex], theme: targetTheme };
              let newItems = [...prev];
              newItems[oldIndex] = updatedItem;

              if (targetThemeItems.length > 0) {
                const lastTargetItem = targetThemeItems[targetThemeItems.length - 1];
                const targetIndex = newItems.findIndex((i) => i.id === lastTargetItem.id);
                newItems = arrayMove(newItems, oldIndex, targetIndex);
              }

              setCustomThemes((prevThemes) =>
                prevThemes.includes(targetTheme)
                  ? prevThemes
                  : [...prevThemes, targetTheme],
              );
              return newItems;
            }
          } else if (direction === "down") {
            if (itemIdxInTheme < itemsInCurrentTheme.length - 1) {
              const swapItem = itemsInCurrentTheme[itemIdxInTheme + 1];
              const oldIndex = prev.findIndex((i) => i.id === id);
              const newIndex = prev.findIndex((i) => i.id === swapItem.id);
              return arrayMove([...prev], oldIndex, newIndex);
            } else if (themeIdx < ordered.length - 1) {
              const targetTheme = ordered[themeIdx + 1];
              const targetThemeItems = groups[targetTheme] || [];
              const oldIndex = prev.findIndex((i) => i.id === id);

              let updatedItem = { ...prev[oldIndex], theme: targetTheme };
              let newItems = [...prev];
              newItems[oldIndex] = updatedItem;

              if (targetThemeItems.length > 0) {
                const firstTargetItem = targetThemeItems[0];
                const targetIndex = newItems.findIndex((i) => i.id === firstTargetItem.id);
                newItems = arrayMove(newItems, oldIndex, targetIndex);
              }

              setCustomThemes((prevThemes) =>
                prevThemes.includes(targetTheme)
                  ? prevThemes
                  : [...prevThemes, targetTheme],
              );
              return newItems;
            }
          }

          return prev;
        }
      });
      setSortColumn("default");
    },
  );

  const handleMoveMobileUp = useEventCallback((id: string) =>
    handleMoveMobile(id, "up"),
  );
  const handleMoveMobileDown = useEventCallback((id: string) =>
    handleMoveMobile(id, "down"),
  );

  const handleMoveThemeMobile = useEventCallback(
    (themeName: string, direction: "up" | "down") => {
      setCustomThemes((prev) => {
        const groups: Record<string, PortfolioItem[]> = {};
        prev.forEach((t) => (groups[t] = []));
        portfolio.forEach((item) => {
          const theme = resolveThemeForItem(item, prev, Object.keys(groups), t("common.other"));
          if (!groups[theme]) groups[theme] = [];
          groups[theme].push(item);
        });
        const existingThemes = Object.keys(groups);
        let ordered = prev.filter((t) => existingThemes.includes(t));
        const nonCustom = existingThemes.filter(t => !ordered.includes(t)).sort();
        ordered.push(...nonCustom);
        ordered = Array.from(new Set(ordered));

        const olIdx = ordered.indexOf(themeName);
        if (olIdx < 0) return prev;

        const nuIdx = direction === "up" ? olIdx - 1 : olIdx + 1;
        if (nuIdx < 0 || nuIdx >= ordered.length) return prev;

        return arrayMove(ordered, olIdx, nuIdx);
      });
    },
  );

  const handleMoveThemeMobileUp = useEventCallback((themeName: string) =>
    handleMoveThemeMobile(themeName, "up"),
  );
  const handleMoveThemeMobileDown = useEventCallback((themeName: string) =>
    handleMoveThemeMobile(themeName, "down"),
  );

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        let conf = await (await getDbService()).getUserConfig();
        if (!conf) {
          await (await getDbService()).createUserConfig(u.email || undefined);
        }
        await loadPortfolios(undefined, true);
      } else {
        setPortfoliosList([]);
        updateCurrentPortfolioId(null);
        loadDefaultExample();
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isDirty]);

  const loadPortfolios = async (switchToId?: string | boolean, preserveSelectedTicker: boolean = false) => {
    const list = await (await getDbService()).getPortfolios();
    setPortfoliosList(list);
    let conf = await (await getDbService()).getUserConfig();
    if (conf) {
      setDefaultPortfolioId(conf.defaultPortfolioId || null);
    }

    if (list.length > 0) {
      if (switchToId !== false) {
        let targetId;
        if (typeof switchToId === "string") {
          targetId = switchToId;
        } else {
          targetId = conf?.defaultPortfolioId || list[0].id;
        }
        handleSwitchPortfolio(targetId, list, preserveSelectedTicker);
      }
    } else if (switchToId !== false) {
      loadDefaultExample();
    }
  };

  const loadDefaultExample = () => {
    setPortfolio((prev) => {
      if (prev.length > 0 && prev.every((p) => p.id?.startsWith("example-"))) {
        return prev;
      }
      return DEFAULT_EXAMPLE_PORTFOLIO.map((p) => ({ ...p }));
    });
    setCurrentPortfolioName(t("portfolio.example_portfolio_name"));
    setIsExample(true);
    updateCurrentPortfolioId(null);
    setSortColumn("default");
    setViewMode("LIST");
    setShowTechFundDetails(false);
    setMaxRiskPerTradePct(0.01);
    setMaxAllocationCapPct(0.15);
    setTradeDuration('position');
    if (!window.location.pathname.startsWith("/analysis/")) {
      setSelectedTicker(null);
      setActiveAnalysis(null);
    }
    performBulkAnalysis(DEFAULT_EXAMPLE_PORTFOLIO, false, null);
    // Sync URL state after example portfolio is loaded
    setTimeout(() => handleUrlSync(), 0);
  };

  const handleSwitchPortfolio = async (id: string, list?: DbPortfolio[], preserveSelectedTicker: boolean = false) => {
    if (isDirty && id !== currentPortfolioId) {
      const decision = await confirmUnsavedChanges(
        t("portfolio.unsaved_changes_title"),
        t("portfolio.unsaved_changes_message")
      );
      if (decision === 'CANCEL') return;
      if (decision === 'SAVE') {
        await savePortfolioItems(portfolio, { silent: true });
      }
    }
    const targetList = list || portfoliosList;
    const p = targetList.find((x) => x.id === id);
    if (!p) return;

    // Only reset security analysis if not preserving and not currently on an explicit analysis URL
    if (!preserveSelectedTicker && !window.location.pathname.startsWith("/analysis/")) {
      setSelectedTicker(null);
      setActiveAnalysis(null);
    }

    updateCurrentPortfolioId(id);
    setCurrentPortfolioName(p.name);
    setIsExample(false);

    // Sort items if listOrder is present
    let items = p.items.map((item) => ({ ...item }));
    if (p.views?.listOrder && p.views.listOrder.length === items.length) {
      const orderMap = new Map<string, number>(
        p.views.listOrder.map((it, idx) => [it, idx]),
      );
      const allExist = items.every((it) => it.id && orderMap.has(it.id));
      if (allExist) {
        items.sort((a, b) => {
          const aIdx = a.id ? (orderMap.get(a.id) ?? 999) : 999;
          const bIdx = b.id ? (orderMap.get(b.id) ?? 999) : 999;
          return (aIdx as number) - (bIdx as number);
        });
      }
    }

    // Convert DB item to PortfolioItem (with analysis as undefined initially)
    const typedPortfolio: PortfolioItem[] = items.map((item) => ({
      id: item.id,
      ticker: item.ticker,
      allocation: item.allocation,
      theme: item.theme,
    }));

    setCustomThemes(Array.from(new Set((p.views?.customThemes || []).map((t: string) => t.trim().toUpperCase()))));
    if (p.views?.defaultView) {
      setViewMode(p.views.defaultView);
    } else {
      setViewMode("LIST");
    }
    setShowTechFundDetails(p.views?.showTechFundDetails || false);
    const isWatchlist = p.type === "WATCHLIST";
    setMaxRiskPerTradePct(isWatchlist ? 0.01 : (p.maxRiskPerTradePct ?? p.views?.maxRiskPerTradePct ?? 0.01));
    setMaxAllocationCapPct(isWatchlist ? 0.15 : (p.maxAllocationCapPct ?? p.views?.maxAllocationCapPct ?? 0.15));
    setTradeDuration(isWatchlist ? 'position' : (p.tradeDuration ?? p.views?.tradeDuration ?? 'position'));
    setPortfolio(typedPortfolio);
    setSortColumn("default");
    performBulkAnalysis(typedPortfolio, false, id);
    // Sync URL state after portfolio is switched
    setTimeout(() => handleUrlSync(), 0);
  };

  const handleLogin = async (providerType: "google" | "apple" = "google") => {
    if (Capacitor.isNativePlatform()) {
      let fallbackInfo: any = null;
      try {
        if (providerType === "google") {
          let result;
          let fallbackInfo: any = null;
      try {
            const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
            result = await FirebaseAuthentication.signInWithGoogle({ useCredentialManager: true });
          } catch (credError: any) {
            if (
              credError.code === "auth/cancelled-popup-request" ||
              credError.code === "auth/popup-closed-by-user" ||
              credError.message?.toLowerCase().includes("cancel")
            ) {
              return;
            }
            console.warn("Credential Manager failed, falling back to legacy Google Sign-In:", credError);
            const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
            result = await FirebaseAuthentication.signInWithGoogle({ useCredentialManager: false });
          }

          if (!result.credential?.idToken) {
            throw new Error("No ID token returned from Google Sign-In.");
          }

          const credential = GoogleAuthProvider.credential(result.credential.idToken);
          await signInWithCredential(auth, credential);
        } else if (providerType === "apple") {
          const { FirebaseAuthentication } = await import("@capacitor-firebase/authentication");
          const result = await FirebaseAuthentication.signInWithApple();
          if (result.credential?.idToken) {
            const credential = new OAuthProvider("apple.com").credential({
              idToken: result.credential.idToken,
              rawNonce: result.credential.nonce,
            });
            await signInWithCredential(auth, credential);
          }
        }
        return;
      } catch (error: any) {
        if (error.code === "auth/cancelled-popup-request" || error.code === "auth/popup-closed-by-user" || error.message?.toLowerCase().includes("cancel")) {
          return;
        }
        console.error("Native login encountered an error", error);
        alertUser("Error", `Native login error: ${error.message}`);
        return;
      }
    }

    let provider;
    switch (providerType) {
      case "apple":
        provider = new OAuthProvider("apple.com");
        break;
      default:
        provider = new GoogleAuthProvider();
        provider.setCustomParameters({
          prompt: "select_account",
        });
    }

    try {
      await signInWithPopup(auth, provider);
    } catch (error: any) {
      if (
        error.code === "auth/cancelled-popup-request" ||
        error.code === "auth/popup-closed-by-user"
      ) {
        // User closed the popup, safely ignore
        return;
      }
      if (
        (error.message && error.message.includes("missing initial state")) ||
        error.code === "auth/missing-initial-state"
      ) {
        alertUser(
          "Login Session Notice",
          "Safari / browser privacy settings isolated the session storage. Please retry or ensure cross-site tracking is allowed."
        );
        return;
      }
      if (error.code === "auth/operation-not-allowed") {
        alertUser(
          "Error",
          "This login method is not enabled. Please go to the Firebase Console Authentication section and enable it.",
        );
        return;
      }
      console.error("Login encountered an error", error);
      alertUser("Error", `Login error: ${error.message}`);
    }
  };

  const handleLogout = async () => {
    if (isDirty) {
      const decision = await confirmUnsavedChanges(
        t("portfolio.unsaved_changes_title"),
        t("portfolio.unsaved_changes_message")
      );
      if (decision === 'CANCEL') return;
      if (decision === 'SAVE') {
        await savePortfolioItems(portfolio, { silent: true });
      }
    }
    auth.signOut();
  };

  const handleSetDefaultPortfolio = async () => {
    if (!currentPortfolioId) return;
    await (await getDbService()).updateUserConfig(currentPortfolioId);
    setDefaultPortfolioId(currentPortfolioId);
    await alertUser(
      "Success",
      t("portfolio.default_set_success") ||
      "Default portfolio set successfully.",
    );
  };

  // Persistence: Add new portfolio
  const handleAddNewPortfolio = async () => {
    if (!user) {
      handleLogin();
      return;
    }
    const result = await promptPortfolio(
      t("portfolio.new_portfolio_prompt") || "Enter new portfolio name:",
      t("portfolio.new_portfolio_default") || "My Portfolio",
      "REGULAR"
    );
    if (!result) return;
    const { name, type } = result;
    const isWatchlist = type === "WATCHLIST";
    const newPort: DbPortfolio = {
      id: crypto.randomUUID(),
      name,
      type,
      items: [],
      views: {
        maxRiskPerTradePct: 0.01,
        maxAllocationCapPct: 0.15,
        tradeDuration: 'position' as const,
      },
      maxRiskPerTradePct: 0.01,
      maxAllocationCapPct: 0.15,
      tradeDuration: 'position' as const,
    };
    await (await getDbService()).savePortfolio(newPort);
    await loadPortfolios(newPort.id);
  };

  const handleDeletePortfolio = async () => {
    if (!currentPortfolioId) return;

    if (portfolio.length > 0) {
      const currentName =
        portfoliosList.find((p) => p.id === currentPortfolioId)?.name ||
        "this portfolio";
      const msg = `Are you sure you would like to delete "${currentName}"?`;
      const confirmed = await confirmUser("Delete Portfolio", msg, {
        confirmText: "Delete",
        isDanger: true,
      });
      if (!confirmed) return;
    }

    await (await getDbService()).deletePortfolio(currentPortfolioId);

    const remaining = portfoliosList.filter((p) => p.id !== currentPortfolioId);
    if (remaining.length === 0) {
      loadDefaultExample();
      setPortfoliosList([]);
      updateCurrentPortfolioId(null);
    } else {
      await loadPortfolios(remaining[0].id);
    }
  };

  const handleRenamePortfolio = async () => {
    if (!currentPortfolioId) return;

    const dbPort = portfoliosList.find((p) => p.id === currentPortfolioId);
    if (!dbPort) return;

    const currentName = dbPort.name || "My Portfolio";
    const currentType = (dbPort as any).type || "REGULAR";
    const result = await promptPortfolio(
      t("portfolio.rename_prompt", {
        defaultValue: "Edit portfolio settings:",
      }),
      currentName,
      currentType
    );

    if (result && (result.name !== currentName || result.type !== currentType)) {
      if (currentType === "REGULAR" && result.type === "WATCHLIST") {
        const confirmed = await confirmUser("Convert to Watchlist", "All allocations will be deleted. Do you wish to continue?", { isDanger: true });
        if (!confirmed) return;
      }
      const updatedPort = { ...dbPort, name: result.name, type: result.type };
      if (currentType === "REGULAR" && result.type === "WATCHLIST") {
        updatedPort.items = updatedPort.items.map(item => ({ ...item, allocation: "0" }));
        updatedPort.maxRiskPerTradePct = 0.01;
        updatedPort.maxAllocationCapPct = 0.15;
        updatedPort.tradeDuration = 'position';
        updatedPort.views = {
          ...(updatedPort.views || {}),
          maxRiskPerTradePct: 0.01,
          maxAllocationCapPct: 0.15,
          tradeDuration: 'position',
        };
      }
      if (result.type === "WATCHLIST") {
        setMaxRiskPerTradePct(0.01);
        setMaxAllocationCapPct(0.15);
        setTradeDuration('position');
      }
      await (await getDbService()).savePortfolio(updatedPort as any);
      // We don't want to reload taking away unsaved items in DOM, just re-fetch the portfolio list to update names
      const list = await (await getDbService()).getPortfolios();
      setPortfoliosList(list);
      setCurrentPortfolioName(result.name);

      // Update local state items if they were wiped
      if (currentType === "REGULAR" && result.type === "WATCHLIST") {
        setPortfolio(prev => prev.map(item => ({ ...item, allocation: "0" })));
      }
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchQuery.length >= 3) {
        handleSearch();
      } else if (searchQuery.length < 3) {
        setSearchResults([]);
      }
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const handleSearch = async () => {
    if (!searchQuery) return;
    setSearchLoading(true);
    try {
      const res = await apiFetch(
        `/api/search?q=${encodeURIComponent(searchQuery)}`,
      );
      const data = await res.json();
      setSearchResults(data.results || []);
    } catch (err) {
      console.error(err);
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAnalyzeSingle = useEventCallback(
    async (
      ticker: string,
      force: boolean = false,
      hideBanner: boolean = true,
    ) => {
      ticker = normalizeTicker(ticker);
      if (!ticker) return;

      if (hideBanner) {
        setHideTopPickBanner(true);
      }

      if (!force) {
        const cachedData = getAnalysisFromCache(ticker);
        if (cachedData) {
          setActiveAnalysis(cachedData);
          setPortfolio((prev) => {
            const exists = prev.some((p) => p.ticker?.toUpperCase() === ticker.toUpperCase());
            if (!exists) return prev;
            return prev.map((p) =>
              p.ticker?.toUpperCase() === ticker.toUpperCase() ? { ...p, analysis: cachedData } : p,
            );
          });
          setSelectedTicker(ticker);
          setSearchResults([]);
          setSearchQuery("");
          return;
        }
      }

      setSelectedTicker(ticker);
      const existingInPort = portfolio.find((p) => p.ticker?.toUpperCase() === ticker.toUpperCase());
      if (!force && existingInPort?.analysis) {
        setActiveAnalysis(existingInPort.analysis);
      } else if (force) {
        setActiveAnalysis(null);
      }
      setSearchResults([]);
      setSearchQuery("");

      setAnalysisLoading(true);
      let fallbackInfo: any = null;
      try {
        if (force) {
          clearAnalysisCache(ticker);
        }
        const res = await apiFetch(
          `/api/analysis?ticker=${ticker}${force ? "&refresh=true" : ""}&tradeDuration=${tradeDuration}`,
        );
        const data = await res.json();
        if (data.error) throw new Error(data.error);

        saveAnalysisToCache(ticker, data);
        setActiveAnalysis(data);
        setPortfolio((prev) => {
          const exists = prev.some((p) => p.ticker?.toUpperCase() === ticker.toUpperCase());
          if (!exists) return prev;
          return prev.map((p) => (p.ticker?.toUpperCase() === ticker.toUpperCase() ? { ...p, analysis: data } : p));
        });
        if (force) {
          setRefreshTrigger((prev) => prev + 1);
        }
      } catch (err: any) {
        setError(`Failed to analyze ${ticker}: ` + err.message);
        setSelectedTicker(null);
        setActiveAnalysis(null);
        window.history.replaceState({}, "", "/");
      } finally {
        setAnalysisLoading(false);
      }
    },
  );

  const handleUrlSync = useEventCallback(() => {
    const path = window.location.pathname;
    if (path.startsWith("/analysis/")) {
      const ticker = normalizeTicker(path.split("/")[2]?.toUpperCase());
      if (ticker) {
        if (ticker !== selectedTicker || (!analysisLoading && (!activeAnalysis || activeAnalysis.ticker?.toUpperCase() !== ticker.toUpperCase()))) {
          handleAnalyzeSingle(ticker);
        }
      }
    } else if (path === "/" || path === "") {
      if (selectedTicker) setSelectedTicker(null);
    }
  });

  useEffect(() => {
    // Trigger single ticker analysis if landing directly on /analysis/:ticker
    if (selectedTicker && !analysisLoading && (!activeAnalysis || activeAnalysis.ticker?.toUpperCase() !== selectedTicker.toUpperCase())) {
      handleAnalyzeSingle(selectedTicker);
    }
  }, [selectedTicker, activeAnalysis, analysisLoading, handleAnalyzeSingle]);

  useEffect(() => {
    // Initialize secure session for API calls
    apiFetch("/api/session").catch(() => { });

    apiFetch("/api/config")
      .then((res) => res.json())
      .then((data) => setIsProd(!!data.isProd))
      .catch(() => { });

    window.addEventListener("popstate", handleUrlSync);
    return () => window.removeEventListener("popstate", handleUrlSync);
  }, [handleUrlSync]);

  const performBulkAnalysis = async (
    items: PortfolioItem[],
    force: boolean = false,
    overridePortfolioId?: string | null,
  ) => {
    const targetPortfolioId = overridePortfolioId !== undefined ? overridePortfolioId : currentPortfolioId;
    if (force) {
      setLastQuotesFetch(Date.now());
    }
    const tickersToAnalyze = [...items]
      .sort((a, b) => {
        const allocA = typeof a.allocation === "number" ? a.allocation : parseFloat(a.allocation as string) || 0;
        const allocB = typeof b.allocation === "number" ? b.allocation : parseFloat(b.allocation as string) || 0;
        return allocB - allocA;
      })
      .filter((i) => {
        if (force) return i.ticker && i.ticker.trim() !== "";
        return !i.analysis && i.ticker && i.ticker.trim() !== "";
      })
      .map((i) => i.ticker.trim());

    if (tickersToAnalyze.length === 0) return;

    // Check cache for each ticker if not forcing
    const resultsToApply: { ticker: string; data: any }[] = [];
    const remainingTickers: string[] = [];

    if (!force) {
      tickersToAnalyze.forEach((ticker) => {
        const cached = getAnalysisFromCache(ticker);
        if (cached) {
          resultsToApply.push({ ticker, data: cached });
        } else {
          remainingTickers.push(ticker);
        }
      });

      if (resultsToApply.length > 0) {
        if (targetPortfolioId !== currentPortfolioIdRef.current) return;
        setPortfolio((prev) =>
          prev.map((item) => {
            const itemTickerUpper = item.ticker?.trim().toUpperCase();
            const match = resultsToApply.find(
              (r) => r.ticker?.trim().toUpperCase() === itemTickerUpper,
            );
            if (match) return { ...item, analysis: match.data };
            return item;
          }),
        );
      }
    } else {
      remainingTickers.push(...tickersToAnalyze);
      tickersToAnalyze.forEach((t) => clearAnalysisCache(t));
      // Wipe existing analysis state for live analytics to show loading indicators
      if (targetPortfolioId !== currentPortfolioIdRef.current) return;
      const analyzeUpper = tickersToAnalyze.map((t) => t.trim().toUpperCase());
      setPortfolio((prev) =>
        prev.map((item) => {
          if (analyzeUpper.includes(item.ticker?.trim().toUpperCase())) {
            return { ...item, analysis: null };
          }
          return item;
        }),
      );
    }

    if (remainingTickers.length === 0) return;

    // if the fetch fails or takes time. bulkLoading spinner handles the UI feedback.

    const initialLoaded = resultsToApply.length;
    const totalCount = tickersToAnalyze.length;

    setBulkLoading(true);
    setBulkProgress({ loaded: initialLoaded, total: totalCount });

    try {
      let loaded = initialLoaded;
      let i = 0;
      while (i < remainingTickers.length) {
        if (targetPortfolioId !== currentPortfolioIdRef.current) {
          setBulkLoading(false);
          return;
        }
        const currentChunkSize = i === 0 ? 3 : 6;
        const chunk = remainingTickers.slice(i, i + currentChunkSize);
        i += currentChunkSize;
        let fallbackInfo: any = null;
      try {
          const res = await apiFetch("/api/bulk-analysis", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ tickers: chunk, refresh: force, tradeDuration }),
          });

          if (targetPortfolioId !== currentPortfolioIdRef.current) {
            setBulkLoading(false);
            return;
          }

          if (!res.ok) {
            const text = await res.text();
            throw new Error(
              `Server returned ${res.status}: ${text.substring(0, 50)}...`,
            );
          }

          const contentType = res.headers.get("content-type");
          if (!contentType || !contentType.includes("application/json")) {
            const text = await res.text();
            throw new Error(
              `Server returned non-JSON response. Please wait a moment and try again. The background process may be restarting.`,
            );
          }

          const data = await res.json();

          if (data.results) {
            if (targetPortfolioId !== currentPortfolioIdRef.current) {
              setBulkLoading(false);
              return;
            }
            data.results.forEach((r: any) => {
              if (r && !r.error) {
                saveAnalysisToCache(r.ticker, r);
                if (selectedTicker && r.ticker?.trim().toUpperCase() === selectedTicker.trim().toUpperCase()) {
                  setActiveAnalysis(r);
                }
              }
            });

            const chunkThemesToAdd: string[] = [];

            setPortfolio((prev) => {
              if (targetPortfolioId !== currentPortfolioIdRef.current) return prev;
              const chunkUpper = chunk.map((c) => c.trim().toUpperCase());
              const updated = prev.map((item) => {
                const itemTickerUpper = item.ticker?.trim().toUpperCase();
                const result = data.results.find(
                  (r: any) => r && !r.error && r.ticker?.trim().toUpperCase() === itemTickerUpper,
                );
                if (
                  result &&
                  chunkUpper.includes(itemTickerUpper)
                ) {
                  let resolvedTheme = item.theme;
                  if (!resolvedTheme && result.sector) {
                    const tempItem = { ticker: item.ticker, analysis: result };
                    const existingThemes = Array.from(
                      new Set(
                        prev
                          .map((p) => p.theme)
                          .filter(Boolean) as string[],
                      ),
                    );
                    const resolved = resolveThemeForItem(
                      tempItem,
                      customThemes,
                      existingThemes,
                      t("common.other"),
                    );
                    if (resolved && resolved !== t("common.other")) {
                      resolvedTheme = resolved;
                      if (!customThemes.includes(resolved) && !chunkThemesToAdd.includes(resolved)) {
                        chunkThemesToAdd.push(resolved);
                      }
                    }
                  }
                  return { ...item, analysis: result };
                }
                return item;
              });

              return updated;
            });

            if (chunkThemesToAdd.length > 0) {
              setCustomThemes((prev) => Array.from(new Set([...prev, ...chunkThemesToAdd])));
            }
          }
        } catch (err: any) {
          console.error(
            `Bulk analysis failed for chunk ${i}`,
            err.message || err,
          );
        }

        loaded += chunk.length;
        setBulkProgress({ loaded: Math.min(totalCount, loaded), total: totalCount });

        // Add a small delay to avoid rate limiting
        if (i < remainingTickers.length) {
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
      }
    } finally {
      if (targetPortfolioId === currentPortfolioIdRef.current) {
        setBulkLoading(false);
        if (force && selectedTicker && tickersToAnalyze.map(t => t.toUpperCase()).includes(selectedTicker.toUpperCase())) {
          setRefreshTrigger((prev) => prev + 1);
        }
      }
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLoading(true);
    setError(null);
    setSelectedTicker(null);
    setSortColumn("default");

    const processData = (rows: any[][]) => {
      const validSymbolRegex = /^[A-Z0-9\-\.]+$/i;

      let startIndex = 0;
      let tickerIdx = 0;
      let allocIdx = 2; // Default if no header
      let themeIdx = -1;

      if (rows.length > 0) {
        const headerRow = rows[0].map((h) =>
          String(h || "")
            .trim()
            .toUpperCase(),
        );
        const expectedTicker = (
          t("common.csv.ticker") || "TICKER"
        ).toUpperCase();
        const expectedAlloc = (
          t("common.csv.allocation") || "ALLOCATION"
        ).toUpperCase();
        const expectedGroup = (
          t("common.csv.group_name") || "GROUP NAME"
        ).toUpperCase();

        if (
          headerRow.includes("TICKER") ||
          headerRow.includes("SYMBOL") ||
          headerRow.includes(expectedTicker)
        ) {
          startIndex = 1;
          tickerIdx = Math.max(
            0,
            headerRow.findIndex(
              (h) => h === "TICKER" || h === "SYMBOL" || h === expectedTicker,
            ),
          );

          allocIdx = headerRow.findIndex(
            (h) =>
              h.includes("ALLOC") ||
              h.includes("WEIGHT") ||
              h === expectedAlloc,
          );
          if (allocIdx === -1) {
            allocIdx = headerRow.length > 2 ? 2 : 1;
          }

          themeIdx = headerRow.findIndex(
            (h) =>
              h.includes("GROUP") ||
              h.includes("THEME") ||
              h.includes("CATEGORY") ||
              h === expectedGroup,
          );
        } else if (String(rows[0][0]).trim().toUpperCase() === "TICKER") {
          startIndex = 1;
        }
      }

      setPortfolio((prev) => {
        const newPortfolio: PortfolioItem[] = [];
        const themesToKeep = new Set<string>();

        for (let i = startIndex; i < rows.length; i++) {
          const row = rows[i];
          if (!row || row.length === 0) continue;

          const tickerRaw = row[tickerIdx];
          if (tickerRaw === undefined) continue;

          const ticker = normalizeTicker(String(tickerRaw).trim().toUpperCase());
          if (ticker && ticker !== "TICKER") {
            let rawAllocation = "0";
            if (allocIdx !== -1 && row[allocIdx] !== undefined) {
              rawAllocation = String(row[allocIdx]).trim();
            } else if (row.length === 2 && allocIdx === -1) {
              rawAllocation = String(row[1]).trim();
            }
            let allocation = "0";
            const numVal = parseFloat(rawAllocation.replace(/[^0-9.-]+/g, ""));
            if (!isNaN(numVal)) {
              const clamped = numVal > 100.5 ? 100.5 : numVal;
              allocation = String(Math.round(clamped * 10) / 10);
            }

            let theme;
            if (
              themeIdx !== -1 &&
              row[themeIdx] !== undefined &&
              String(row[themeIdx]).trim()
            ) {
              theme = String(row[themeIdx]).trim();
              themesToKeep.add(theme);
            }

            if (!newPortfolio.find((p) => p.ticker === ticker)) {
              const existing = prev.find((ext: any) => ext.ticker === ticker);
              newPortfolio.push({
                id: existing?.id || crypto.randomUUID(),
                ticker,
                allocation: allocation || "0",
                theme: theme || existing?.theme,
                analysis: existing?.analysis,
              });
            }
          }
        }

        if (newPortfolio.length === 0) {
          setError(t("common.errors.no_symbols_found"));
          setLoading(false);
          return prev;
        }

        if (themesToKeep.size > 0) {
          setCustomThemes((prevThemes) => {
            const newThemes = [...prevThemes];
            themesToKeep.forEach((tName) => {
              if (!newThemes.includes(tName)) newThemes.push(tName);
            });
            return newThemes;
          });
        }

        const merged = isExample
          ? newPortfolio
          : [
            ...prev,
            ...newPortfolio.filter(
              (np) => !prev.find((p) => p.ticker === np.ticker),
            ),
          ];

        setTimeout(() => performBulkAnalysis(merged), 0);
        setLoading(false);
        setIsExample(false);
        e.target.value = "";
        return merged;
      });
    };

    if (file.name.endsWith(".csv") || file.name.match(/\.xlsx?$/)) {
      const reader = new FileReader();
      reader.onload = async (evt) => {
        let fallbackInfo: any = null;
      try {
          const XLSX = await import("xlsx");
          const data = evt.target?.result;
          const workbook = XLSX.read(data, { type: file.name.endsWith(".csv") ? "string" : "binary" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const json = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
          }) as any[][];
          processData(json);
        } catch (err: any) {
          setError(`${t("common.errors.failed_parse_csv")}${err.message}`);
          setLoading(false);
        }
      };
      reader.onerror = () => {
        setError("Failed to read file.");
        setLoading(false);
      };
      if (file.name.endsWith(".csv")) {
        reader.readAsText(file);
      } else {
        reader.readAsBinaryString(file);
      }
    } else {
      setError("Please upload a .csv, .xls, or .xlsx file.");
      setLoading(false);
    }
  };

  const getAnalysis = (ticker: string) => {
    if (!ticker) return null;
    const norm = ticker.toUpperCase();
    if (activeAnalysis && activeAnalysis.ticker?.toUpperCase() === norm) {
      return activeAnalysis;
    }
    const item = portfolio.find((p) => p.ticker?.toUpperCase() === norm);
    if (item?.analysis) return item.analysis;
    return getAnalysisFromCache(ticker);
  };

  const handleUpdateItem = useEventCallback(
    (id: string, field: "ticker" | "allocation", value: string) => {
      if (field === "allocation") {
        if (value !== "") {
          if (!/^\d*\.?\d{0,1}$/.test(value)) {
            return;
          }
          const num = parseFloat(value);
          if (!isNaN(num) && num > 100.5) {
            return;
          }
        }
      }
      setPortfolio((prev) =>
        prev.map((p) => (p.id === id ? { ...p, [field]: value, isTemp: false } : p)),
      );
      if (isExample) setIsExample(false);
    },
  );

  const savePortfolioItems = async (items: PortfolioItem[], opts?: { silent?: boolean }) => {
    let targetPortfolioId = currentPortfolioId;
    let targetPortfolioName = currentPortfolioName;

    if (!targetPortfolioId) {
      if (!opts?.silent) {
        const name = await promptUser(
          t("portfolio.new_portfolio_prompt") || "Enter new portfolio name:",
          t("portfolio.new_portfolio_default") || "My Portfolio",
        );
        if (!name) return;
        targetPortfolioId = crypto.randomUUID();
        targetPortfolioName = name;
      } else {
        targetPortfolioId = crypto.randomUUID();
        targetPortfolioName = t("portfolio.my_portfolio_default") || "My Portfolio";
      }
      updateCurrentPortfolioId(targetPortfolioId);
      setCurrentPortfolioName(targetPortfolioName);
    }

    const itemsToSave = items
      .filter((item) => !item.isTemp && item.ticker)
      .map((item) => {
        const numVal = parseFloat(String(item.allocation).replace(/[^0-9.-]+/g, "")) || 0;
        const clamped = numVal > 100.5 ? 100.5 : numVal;
        const cleanedAlloc = String(Math.round(clamped * 10) / 10);
        return {
          id: item.id || crypto.randomUUID(),
          ticker: item.ticker,
          allocation: cleanedAlloc,
          theme: item.theme,
        };
      });

    if (itemsToSave.length === 0) return;

    if (!opts?.silent) {
      setLoading(true);
      setError(null);
    }

    const existingPort = portfoliosList.find(p => p.id === targetPortfolioId);
    const port: DbPortfolio = {
      id: targetPortfolioId,
      name: targetPortfolioName,
      type: existingPort?.type || 'REGULAR',
      items: itemsToSave,
      maxRiskPerTradePct,
      maxAllocationCapPct,
      tradeDuration,
      views: {
        listOrder: items.filter((i) => !i.isTemp && i.ticker).map((i) => i.id || ""),
        customThemes,
        defaultView: viewMode,
        showTechFundDetails,
        maxRiskPerTradePct,
        maxAllocationCapPct,
        tradeDuration,
      },
    };

    setPortfoliosList((prevList) => {
      const exists = prevList.some((p) => p.id === targetPortfolioId);
      if (exists) {
        return prevList.map((p) => (p.id === targetPortfolioId ? port : p));
      } else {
        return [...prevList, port];
      }
    });

    if (user) {
      let fallbackInfo: any = null;
      try {
        await (await getDbService()).savePortfolio(port);
        const list = await (await getDbService()).getPortfolios();
        setPortfoliosList(list);

        if (!opts?.silent) {
          alertUser(
            "Success",
            t("portfolio.save_success") || "Portfolio saved successfully.",
          );
        }
      } catch (err: any) {
        if (!opts?.silent) {
          setError(`${t("common.errors.failed_save")}${err.message}`);
        }
      } finally {
        if (!opts?.silent) {
          setLoading(false);
        }
      }
    } else {
      if (!opts?.silent) {
        setLoading(false);
      }
    }
  };

  const handleAddTempTickerToPortfolio = useEventCallback(async (ticker: string, allocationInput?: string) => {
    const normalizedTicker = normalizeTicker(ticker);
    const isWatchlist = currentPortfolioType === "WATCHLIST";
    const allocStr = isWatchlist
      ? "0"
      : allocationInput !== undefined && allocationInput.trim() !== ""
        ? allocationInput.trim()
        : "10";

    let nextPortfolio: PortfolioItem[] = [];
    const exists = portfolio.find((p) => p.ticker === normalizedTicker);
    const analysisToAttach =
      (activeAnalysis?.ticker?.toUpperCase() === normalizedTicker ? activeAnalysis : null) ||
      exists?.analysis ||
      getAnalysisFromCache(normalizedTicker);

    if (exists) {
      nextPortfolio = portfolio.map((p) =>
        p.ticker === normalizedTicker
          ? { ...p, isTemp: false, allocation: !isWatchlist ? allocStr : (p.allocation || "0"), analysis: p.analysis || analysisToAttach }
          : p
      );
    } else {
      nextPortfolio = [
        ...portfolio.filter((p) => p.ticker !== normalizedTicker),
        {
          id: crypto.randomUUID(),
          ticker: normalizedTicker,
          allocation: allocStr,
          isTemp: false,
          analysis: analysisToAttach,
        },
      ];
    }

    setPortfolio(nextPortfolio);
    if (isExample) setIsExample(false);

    await savePortfolioItems(nextPortfolio, { silent: true });
  });

  const handleAddRow = useEventCallback(async (newTicker?: string, allocationInput?: string) => {
    const rawTicker = (newTicker || "").trim();
    if (!rawTicker) return;

    const normalizedTicker = normalizeTicker(rawTicker);

    const existingItem = portfolio.find((p) => p.ticker === normalizedTicker);
    if (existingItem) {
      if (existingItem.isTemp) {
        const isWatchlist = currentPortfolioType === "WATCHLIST";
        const allocStr = allocationInput !== undefined ? String(allocationInput) : existingItem.allocation || "0";
        let updatedPortfolio: PortfolioItem[] = [];
        setPortfolio((prev) => {
          updatedPortfolio = prev.map((p) =>
            p.ticker === normalizedTicker
              ? { ...p, isTemp: false, allocation: !isWatchlist ? allocStr : p.allocation }
              : p
          );
          return updatedPortfolio;
        });
        if (isExample) setIsExample(false);
        savePortfolioItems(updatedPortfolio, { silent: true });
      }
      handleAnalyzeSingle(normalizedTicker);
      return;
    }

    const newId = crypto.randomUUID();
    const allocStr = allocationInput !== undefined ? String(allocationInput) : "0";

    const newItem: PortfolioItem = {
      id: newId,
      ticker: normalizedTicker,
      allocation: allocStr,
      isTemp: false,
    };

    let nextPortfolio: PortfolioItem[] = [];
    setPortfolio((prev) => {
      nextPortfolio = [...prev, newItem];
      return nextPortfolio;
    });

    if (isExample) setIsExample(false);

    // Auto-save silently to DB & sync portfoliosList so isDirty stays false
    await savePortfolioItems(nextPortfolio, { silent: true });

    try {
      const res = await apiFetch(`/api/analysis?ticker=${normalizedTicker}&tradeDuration=${tradeDuration}`);
      const data = await res.json();

      let resolvedTheme: string | undefined;
      if (data && data.sector) {
        const tempItem = { ticker: normalizedTicker, analysis: data };
        const existingThemes = Array.from(new Set(nextPortfolio.map(p => p.theme).filter(Boolean) as string[]));
        const resolved = resolveThemeForItem(
          tempItem,
          customThemes,
          existingThemes,
          t("common.other")
        );
        if (resolved && resolved !== t("common.other")) {
          resolvedTheme = resolved;
        }
      }

      let updatedPortfolio: PortfolioItem[] = [];
      setPortfolio((prev) => {
        updatedPortfolio = prev.map((p) =>
          p.id === newId
            ? {
              ...p,
              analysis: data,
              theme: p.theme || resolvedTheme,
            }
            : p,
        );
        return updatedPortfolio;
      });

      if (resolvedTheme) {
        setCustomThemes((prevThemes) => {
          const nextCustomThemes = prevThemes.includes(resolvedTheme!) ? prevThemes : [...prevThemes, resolvedTheme!];
          savePortfolioItems(updatedPortfolio, { silent: true });
          return nextCustomThemes;
        });
      } else {
        savePortfolioItems(updatedPortfolio, { silent: true });
      }
    } catch (err: any) {
      setPortfolio((prev) =>
        prev.map((p) =>
          p.id === newId
            ? {
              ...p,
              analysis: {
                error: err.message,
                ticker: normalizedTicker,
                technical: {},
                fundamental: {},
              } as any,
            }
            : p,
        ),
      );
    }
  });

  const handleUpdateTicker = useEventCallback(
    async (id: string, newTicker: string) => {
      const normalizedTicker = normalizeTicker(newTicker);
      const item = portfolio.find((p) => p.id === id);
      if (item && item.ticker === selectedTicker) {
        setSelectedTicker(normalizedTicker);
      }

      setPortfolio((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, ticker: normalizedTicker, analysis: undefined, isTemp: false } : p,
        ),
      );
      if (isExample) setIsExample(false);

      let fallbackInfo: any = null;
      try {
        const res = await apiFetch(`/api/analysis?ticker=${normalizedTicker}&tradeDuration=${tradeDuration}`);
        const data = await res.json();

        let resolvedTheme: string | undefined;
        if (data && data.sector) {
          const tempItem = { ticker: normalizedTicker, analysis: data };
          const existingThemes = Array.from(new Set(portfolio.map(p => p.theme).filter(Boolean) as string[]));
          const resolved = resolveThemeForItem(
            tempItem,
            customThemes,
            existingThemes,
            t("common.other")
          );
          if (resolved && resolved !== t("common.other")) {
            resolvedTheme = resolved;
          }
        }

        if (resolvedTheme) {
          setCustomThemes((prevThemes) =>
            prevThemes.includes(resolvedTheme!) ? prevThemes : [...prevThemes, resolvedTheme!],
          );
        }

        setPortfolio((prev) =>
          prev.map((p) =>
            p.id === id
              ? {
                ...p,
                analysis: data,
                theme: p.theme || resolvedTheme,
              }
              : p,
          ),
        );
      } catch (err: any) {
        setPortfolio((prev) =>
          prev.map((p) =>
            p.id === id
              ? {
                ...p,
                analysis: {
                  error: err.message,
                  ticker: normalizedTicker,
                  technical: {},
                  fundamental: {},
                } as any,
              }
              : p,
          ),
        );
      }
    },
  );

  const handleRemoveItem = useEventCallback((id: string, ticker: string) => {
    setPortfolio((prev) => prev.filter((p) => p.id !== id));
    if (isExample) setIsExample(false);
    if (selectedTicker === ticker) {
      setSelectedTicker(null);
      setActiveAnalysis(null);
      // Immediately clear the URL to prevent any sync logic from re-adding the ticker
      if (window.location.pathname.startsWith("/analysis/")) {
        window.history.pushState({}, "", "/");
      }
    }
  });

  const handleSave = async () => {
    if (!user) {
      handleLogin();
      return;
    }
    await savePortfolioItems(portfolio, { silent: false });
  };

  const handleExportCSV = () => {
    if (portfolio.length === 0) return;

    const headers = [
      t("common.csv.ticker"),
      t("common.csv.ticker_name"),
      t("common.csv.allocation"),
    ];

    if (viewMode === "THEME") {
      headers.push(t("common.csv.group_name"));
    }

    const existingThemes: string[] = [];
    const rows = portfolio.map((item) => {
      const a = item.analysis;
      const row = [item.ticker, a?.name || "", item.allocation];
      if (viewMode === "THEME") {
        const theme = resolveThemeForItem(item, customThemes, existingThemes, t("common.other"));
        if (!existingThemes.includes(theme)) {
          existingThemes.push(theme);
        }
        row.push(theme);
      }
      return row;
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row
          .map((v) => (typeof v === "string" && v.includes(",") ? `"${v}"` : v))
          .join(","),
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = `portfolio_export_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  };

  const currentAnalysis = useMemo(() => {
    return selectedTicker ? getAnalysis(selectedTicker) : null;
  }, [selectedTicker, activeAnalysis, portfolio]);

  const { totalAllocation, hasDecimals } = useMemo(() => {
    let hasDecimals = false;
    const total = portfolio.reduce((acc, item) => {
      if (item.allocation.includes(".")) hasDecimals = true;
      const val = parseFloat(item.allocation.replace(/[^0-9.-]+/g, ""));
      return acc + (isNaN(val) ? 0 : val);
    }, 0);
    return { totalAllocation: total, hasDecimals };
  }, [portfolio]);

  const currentPortfolio = portfoliosList.find((p) => p.id === currentPortfolioId);
  const currentPortfolioType = (currentPortfolio as any)?.type || "REGULAR";

  const getEffectiveConfluenceRec = React.useCallback((item: any) => {
    if (!item?.analysis) return "NEUTRAL";
    const isHeldInRegular = currentPortfolioType !== "WATCHLIST" && (parseFloat(String(item.allocation).replace(/[^0-9.-]+/g, "")) || 0) > 0;
    const isTaSell = item.analysis.technical?.recommendation?.includes("SELL");
    const daysToNextEarnings = item.analysis.technical?.daysToNextEarnings ?? item.analysis.technical?.indicators?.daysToNextEarnings ?? (typeof item.analysis.daysToNextEarnings === 'number' ? item.analysis.daysToNextEarnings : null);
    const isEarningsRisk = Boolean(item.analysis.technical?.isEarningsOverride || item.analysis.technical?.indicators?.isEarningsOverride || item.analysis.isEarningsOverride || (typeof daysToNextEarnings === 'number' && daysToNextEarnings !== null && daysToNextEarnings < 7));
    const isShortSwingTakeProfit = !isEarningsRisk && isHeldInRegular && tradeDuration === "short_swing" && (item.analysis.confluence?.conviction <= 60 || item.analysis.confluence?.recommendation === "NEUTRAL") && isTaSell;
    return isEarningsRisk ? "NEUTRAL" : (isShortSwingTakeProfit ? "SELL" : (item.analysis.confluence?.recommendation || "NEUTRAL"));
  }, [currentPortfolioType, tradeDuration]);

  const sortedPortfolio = useMemo(() => {
    if (sortColumn === "default") return portfolio;
    return [...portfolio].sort((a, b) => {
      if (sortColumn === "ticker") {
        const cmp = a.ticker.localeCompare(b.ticker);
        return sortDirection === "desc" ? -cmp : cmp;
      }

      if (sortColumn === "allocation") {
        const valA =
          parseFloat(a.allocation.replace(/[^0-9.-]+/g, "")) || 0;
        const valB =
          parseFloat(b.allocation.replace(/[^0-9.-]+/g, "")) || 0;
        const cmp = valA - valB;
        return sortDirection === "desc" ? -cmp : cmp;
      }

      if (sortColumn === "perf1D") {
        const valA = liveQuotes[a.ticker]?.perf1D ?? a.analysis?.perf1D ?? (typeof a.analysis?.regularMarketChangePercent === 'number' ? a.analysis.regularMarketChangePercent / 100 : -Infinity);
        const valB = liveQuotes[b.ticker]?.perf1D ?? b.analysis?.perf1D ?? (typeof b.analysis?.regularMarketChangePercent === 'number' ? b.analysis.regularMarketChangePercent / 100 : -Infinity);
        const cmp = valA - valB;
        return sortDirection === "desc" ? -cmp : cmp;
      }

      if (!a.analysis && !b.analysis) return 0;
      if (!a.analysis) return 1;
      if (!b.analysis) return -1;

      const perfMetrics = [
        "ytdReturn",
        "perf1W",
        "perf1M",
        "perf3M",
        "perf6M",
        "perf1Y",
      ] as const;
      if (perfMetrics.includes(sortColumn as any)) {
        const field = sortColumn as (typeof perfMetrics)[number];
        const valA = a.analysis[field] ?? -Infinity;
        const valB = b.analysis[field] ?? -Infinity;
        const cmp = valA - valB;
        return sortDirection === "desc" ? -cmp : cmp;
      }

      const getScore = (rec: string) => {
        const upper = rec.toUpperCase();
        if (upper.includes("BUY")) return upper.includes("STRONG") ? 2 : 1;
        if (upper.includes("SELL")) return upper.includes("STRONG") ? -2 : -1;
        return 0;
      };

      const techA = getScore(a.analysis.technical?.recommendation || "NEUTRAL");
      const techB = getScore(b.analysis.technical?.recommendation || "NEUTRAL");
      const fundA = getScore(a.analysis.fundamental?.recommendation || "NEUTRAL");
      const fundB = getScore(b.analysis.fundamental?.recommendation || "NEUTRAL");
      const overallA = getScore(getEffectiveConfluenceRec(a));
      const overallB = getScore(getEffectiveConfluenceRec(b));

      if (sortColumn === "overall") {
        const diff = overallA - overallB;
        if (diff !== 0) return sortDirection === "desc" ? -diff : diff;
        const convA = a.analysis.confluence?.conviction ?? 0;
        const convB = b.analysis.confluence?.conviction ?? 0;
        const convDiff = convA - convB;
        if (convDiff !== 0) return sortDirection === "desc" ? -convDiff : convDiff;
        return 0;
      } else if (sortColumn === "technical") {
        const diff = techA - techB;
        if (diff !== 0) return sortDirection === "desc" ? -diff : diff;
        const convA = a.analysis.technical?.conviction ?? 0;
        const convB = b.analysis.technical?.conviction ?? 0;
        const convDiff = convA - convB;
        if (convDiff !== 0) return sortDirection === "desc" ? -convDiff : convDiff;
        return 0;
      } else if (sortColumn === "fundamental") {
        const diff = fundA - fundB;
        if (diff !== 0) return sortDirection === "desc" ? -diff : diff;
        const convA = a.analysis.fundamental?.conviction ?? 0;
        const convB = b.analysis.fundamental?.conviction ?? 0;
        const convDiff = convA - convB;
        if (convDiff !== 0) return sortDirection === "desc" ? -convDiff : convDiff;
        return 0;
      }

      return 0;
    });
  }, [portfolio, sortColumn, sortDirection, liveQuotes, currentPortfolioType, tradeDuration, getEffectiveConfluenceRec]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === "desc") {
        setSortDirection("asc");
      } else {
        setSortColumn("default");
        setSortDirection("desc");
      }
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const groupedPortfolio = useMemo(() => {
    const groups: Record<string, PortfolioItem[]> = {};
    customThemes.forEach((t) => (groups[t] = []));
    sortedPortfolio.forEach((item) => {
      const theme = resolveThemeForItem(item, customThemes, Object.keys(groups), t("common.other"));
      if (!groups[theme]) groups[theme] = [];
      groups[theme].push(item);
    });
    // Cleanup unused custom themes to prevent empty ghosts, but keep explicitly added custom categories if empty
    return groups;
  }, [portfolio, sortedPortfolio, viewMode, customThemes, t]);

  const orderedThemes = useMemo(() => {
    const existingThemes = Object.keys(groupedPortfolio);
    let ordered = customThemes.filter((theme) =>
      existingThemes.includes(theme),
    );
    const nonCustom = existingThemes.filter(theme => !ordered.includes(theme)).sort();
    ordered.push(...nonCustom);
    return Array.from(new Set(ordered));
  }, [groupedPortfolio, customThemes]);

  const handleUpdateTheme = (oldTheme: string, newTheme: string) => {
    setPortfolio((prev) => {
      const existingThemes: string[] = [];
      return prev.map((item) => {
        const currentTheme = resolveThemeForItem(item, customThemes, existingThemes, t("common.other"));
        if (!existingThemes.includes(currentTheme)) {
          existingThemes.push(currentTheme);
        }
        if (currentTheme === oldTheme) {
          return { ...item, theme: newTheme };
        }
        return item;
      });
    });
    // Replace old theme with new theme in customThemes if it exists there
    setCustomThemes((prev) => Array.from(new Set(prev.map((c) => (c === oldTheme ? newTheme.trim().toUpperCase() : c)))));
  };

  const handleAddCategory = () => {
    const defaultName = (t("portfolio.new_category") || "New Theme").trim().toUpperCase();
    let newName = defaultName;
    let counter = 1;
    while (groupedPortfolio[newName] || customThemes.includes(newName)) {
      newName = `${defaultName} ${counter}`;
      counter++;
    }
    setCustomThemes((prev) => Array.from(new Set([newName, ...prev])));
  };

  const handleDeleteCategory = (themeName: string) => {
    if (
      !groupedPortfolio[themeName] ||
      groupedPortfolio[themeName].length === 0
    ) {
      setCustomThemes((prev) => prev.filter((c) => c !== themeName));
    }
  };

  // URL Sync: Read from URL on mount and handle navigation
  useEffect(() => {
    handleUrlSync();
    window.addEventListener("popstate", handleUrlSync);
    return () => window.removeEventListener("popstate", handleUrlSync);
  }, []); // Only run on mount to setup listeners. Logic moved to handleUrlSync.

  // URL Sync: Update URL when selectedTicker changes
  const isInitialUrlSync = useRef(true);
  useEffect(() => {
    if (isInitialUrlSync.current) {
      isInitialUrlSync.current = false;
      return;
    }
    const currentPath = window.location.pathname;
    if (selectedTicker) {
      const targetPath = `/analysis/${selectedTicker}`;
      if (currentPath !== targetPath) {
        window.history.pushState({ ticker: selectedTicker }, "", targetPath);
      }
    } else if (currentPath.startsWith("/analysis/")) {
      window.history.pushState({}, "", "/");
    }
  }, [selectedTicker]);

  const handleUpdatePortfolioRiskSettings = React.useCallback(async (maxRiskPct: number, maxCapPct: number, duration: TradeDuration) => {
    setMaxRiskPerTradePct(maxRiskPct);
    setMaxAllocationCapPct(maxCapPct);
    setTradeDuration(duration);

    try {
      localStorage.setItem("maxRiskPerTradePct", String(maxRiskPct));
      localStorage.setItem("maxAllocationCapPct", String(maxCapPct));
      localStorage.setItem("tradeDuration", duration);
    } catch { }

    if (currentPortfolioId) {
      let updatedPort: DbPortfolio | null = null;
      setPortfoliosList((prev) =>
        prev.map((p) => {
          if (p.id === currentPortfolioId) {
            updatedPort = {
              ...p,
              maxRiskPerTradePct: maxRiskPct,
              maxAllocationCapPct: maxCapPct,
              tradeDuration: duration,
              views: {
                ...p.views,
                maxRiskPerTradePct: maxRiskPct,
                maxAllocationCapPct: maxCapPct,
                tradeDuration: duration,
              },
            };
            return updatedPort;
          }
          return p;
        })
      );

      if (user && updatedPort) {
        let fallbackInfo: any = null;
      try {
          await (await getDbService()).savePortfolio(updatedPort);
        } catch (err) {
          console.error("Auto-saving portfolio risk parameters failed:", err);
        }
      }
    }
  }, [currentPortfolioId, user]);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-zinc-400 font-sans flex flex-col overflow-x-hidden">
      <SEO ticker={selectedTicker} sector={currentAnalysis?.sector} />
      {ModalComponents}
      <AndroidInstallBanner />
      <IosInstallBanner />
      <div className={`${isChartMaximized ? 'w-full px-4 sm:px-6' : 'max-w-[1200px] p-6 md:p-8 mx-auto w-full'} space-y-3 sm:space-y-4 transition-all duration-300`}>
        <div className="sr-only">
          <h1>{selectedTicker ? `${selectedTicker} Stock Analysis & Fair Value` : t("accessibility.header_title")}</h1>
          <p>{t("accessibility.header_desc")}</p>
        </div>

        {!isChartMaximized && (
          <>
            <AppHeader searchInputRef={searchInputRef} searchQuery={searchQuery} setSearchQuery={setSearchQuery} handleSearch={handleSearch} searchLoading={searchLoading} searchResults={searchResults} handleAnalyzeSingle={handleAnalyzeSingle} currentPortfolioId={currentPortfolioId} handleSwitchPortfolio={handleSwitchPortfolio} portfoliosList={portfoliosList} handleAddNewPortfolio={handleAddNewPortfolio} loading={loading} handleFileUpload={handleFileUpload} user={user} isExample={isExample} handleRenamePortfolio={handleRenamePortfolio} handleSetDefaultPortfolio={handleSetDefaultPortfolio} defaultPortfolioId={defaultPortfolioId} handleDeletePortfolio={handleDeletePortfolio} handleLogin={handleLogin} handleLogout={handleLogout} />

            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-red-950/20 border border-red-500/30 text-red-400 p-4 rounded-sm flex items-center gap-3 text-sm italic"
              >
                <AlertCircle className="shrink-0" size={18} />
                <span>{error}</span>
              </motion.div>
            )}

            <NewsTicker />
            {/* Top Picks Banner - Only show if not hiding and not currently viewing an analysis */}
            {!hideTopPickBanner && !selectedTicker && (
              <TopPickBanner topPickInfo={topPickInfo} loading={topPickLoading} hideTopPickBanner={hideTopPickBanner} selectedTicker={selectedTicker} handleAnalyzeSingle={handleAnalyzeSingle} />
            )}
          </>
        )}

        <main id="main-content" className="flex flex-col lg:flex-row gap-6">
          <PortfolioPanel portfolioType={currentPortfolioType} isChartMaximized={isChartMaximized} selectedTicker={selectedTicker} viewMode={viewMode} sortColumn={sortColumn} sortDirection={sortDirection} setSortColumn={setSortColumn} setSortDirection={setSortDirection} setViewMode={setViewMode} showTechFundDetails={showTechFundDetails} setShowTechFundDetails={setShowTechFundDetails} performBulkAnalysis={performBulkAnalysis} portfolio={portfolio} loading={loading} bulkLoading={bulkLoading} bulkProgress={bulkProgress} handleSave={handleSave} isDirty={isDirty} handleExportCSV={handleExportCSV} isExample={isExample} sensors={sensors} handleDragStart={handleDragStart} handleDragEnd={handleDragEnd} handleDragCancel={handleDragCancel} activeDragId={activeDragId} sortedPortfolio={sortedPortfolio} liveQuotes={liveQuotes} handleAnalyzeSingle={handleAnalyzeSingle} topPickInfo={topPickInfo} handleSort={handleSort} orderedThemes={orderedThemes} groupedPortfolio={groupedPortfolio} handleMoveThemeMobileUp={handleMoveThemeMobileUp} handleMoveThemeMobileDown={handleMoveThemeMobileDown} handleUpdateTheme={handleUpdateTheme} hasDecimals={hasDecimals} handleDeleteCategory={handleDeleteCategory} handleUpdateTicker={handleUpdateTicker} handleUpdateItem={handleUpdateItem} handleRemoveItem={handleRemoveItem} handleMoveMobileUp={handleMoveMobileUp} handleMoveMobileDown={handleMoveMobileDown} listPerfMetric={listPerfMetric} setListPerfMetric={setListPerfMetric} handleAddCategory={handleAddCategory} handleAddRow={handleAddRow} totalAllocation={totalAllocation} TickerEditor={TickerEditor} maxRiskPerTradePct={currentPortfolioType === "WATCHLIST" ? 0.01 : maxRiskPerTradePct} maxAllocationCapPct={currentPortfolioType === "WATCHLIST" ? 0.15 : maxAllocationCapPct} tradeDuration={currentPortfolioType === "WATCHLIST" ? 'position' : tradeDuration} handleUpdatePortfolioRiskSettings={handleUpdatePortfolioRiskSettings} />

          <React.Suspense fallback={
            <div className={`flex-1 min-h-[400px] bg-zinc-900/50 border border-zinc-800 rounded-sm p-6 flex items-center justify-center text-zinc-400 text-xs font-mono ${selectedTicker ? "flex" : "hidden lg:flex"}`}>
              Loading Analysis Engine...
            </div>
          }>
            <AnalysisPanel isChartMaximized={isChartMaximized} selectedTicker={selectedTicker} setSelectedTicker={setSelectedTicker} setIsChartMaximized={setIsChartMaximized} analysisLoading={analysisLoading} currentAnalysis={currentAnalysis} topPickInfo={topPickInfo} analysisTab={analysisTab} setAnalysisTab={setAnalysisTab} profileData={profileData} sentimentData={sentimentData} tabLoading={tabLoading} valuationData={valuationData} isProd={isProd} newsData={newsData} eventsData={eventsData} insightsData={insightsData} isExecSummaryExpanded={isExecSummaryExpanded} setIsExecSummaryExpanded={setIsExecSummaryExpanded} liveQuotes={liveQuotes} listPerfMetric={listPerfMetric} setListPerfMetric={setListPerfMetric} handleAnalyzeSingle={handleAnalyzeSingle} portfolio={portfolio} tradeDuration={currentPortfolioType === "WATCHLIST" ? 'position' : tradeDuration} handleAddTempTickerToPortfolio={handleAddTempTickerToPortfolio} currentPortfolioType={currentPortfolioType} maxRiskPerTradePct={currentPortfolioType === "WATCHLIST" ? 0.01 : maxRiskPerTradePct} maxAllocationCapPct={currentPortfolioType === "WATCHLIST" ? 0.15 : maxAllocationCapPct} />
          </React.Suspense>
        </main>

        <footer className="pt-12 pb-10 border-t border-zinc-800 mt-12 flex flex-col md:flex-row justify-between items-center gap-6 text-zinc-400">
          <div className="flex flex-col gap-3 items-center md:items-start">
            <p className="text-[9px] uppercase tracking-widest text-zinc-400 flex flex-wrap items-center gap-2 justify-center md:justify-start">
              <span>{t("footer.copyright", { year: new Date().getFullYear() })}</span>
              <span className="text-zinc-400 font-mono border-l border-zinc-800 pl-2 lowercase normal-case">
                ({t("analysis.engine_version")})
              </span>
            </p>
            <div className="flex flex-wrap gap-4 justify-center md:justify-start">
              <a
                href="/features"
                className="text-[9px] uppercase tracking-widest text-zinc-400 hover:text-emerald-400 transition-colors"
              >
                {t("footer.features")}
              </a>
              <a
                href="/methodology"
                className="text-[9px] uppercase tracking-widest text-zinc-400 hover:text-emerald-400 transition-colors"
              >
                {t("footer.methodology")}
              </a>
              <a
                href="/faq"
                className="text-[9px] uppercase tracking-widest text-zinc-400 hover:text-emerald-400 transition-colors"
              >
                {t("footer.faq")}
              </a>
              <a
                href="/about"
                className="text-[9px] uppercase tracking-widest text-zinc-400 hover:text-emerald-400 transition-colors"
              >
                {t("footer.about_us")}
              </a>
              <a
                href="/terms"
                className="text-[9px] uppercase tracking-widest text-zinc-400 hover:text-emerald-400 transition-colors"
              >
                {t("footer.tos")}
              </a>
              <a
                href="/privacy"
                className="text-[9px] uppercase tracking-widest text-zinc-400 hover:text-emerald-400 transition-colors"
              >
                {t("footer.privacy_policy")}
              </a>
            </div>
            <div className="flex flex-wrap gap-3 justify-center md:justify-start text-[8px] uppercase tracking-widest text-zinc-400">
              <span className="text-zinc-400 font-mono">Guides:</span>
              <a href="/guides/market-regimes" className="text-zinc-400 hover:text-emerald-400 transition-colors">Market Regimes</a>
              <a href="/guides/saas-rule-of-40" className="text-zinc-400 hover:text-emerald-400 transition-colors">SaaS Rule of 40</a>
              <a href="/guides/dcf-calculator" className="text-zinc-400 hover:text-emerald-400 transition-colors">Cycle-Normalized DCF</a>
              <a href="/guides/roic-analysis" className="text-zinc-400 hover:text-emerald-400 transition-colors">Capital Efficiency & ROIC</a>
            </div>
            <div className="flex flex-wrap gap-2 justify-center md:justify-start text-[8px] uppercase tracking-widest text-zinc-400 mt-1">
              <span className="text-zinc-400 font-mono">Popular Valuations:</span>
              {["AAPL", "MSFT"].map((ticker) => (
                <a
                  key={ticker}
                  href={`/analysis/${ticker}`}
                  className="text-zinc-400 hover:text-emerald-400 transition-colors font-mono"
                >
                  {ticker}
                </a>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-5 text-zinc-400">
            <a
              href="https://www.linkedin.com/company/stock-investing-pro/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-emerald-400 hover:scale-110 transition-all duration-200 flex items-center justify-center p-1.5 rounded-full border border-zinc-800 hover:border-emerald-500/30 bg-zinc-950/40"
              aria-label="LinkedIn"
            >
              <Linkedin size={16} />
            </a>
            <a
              href="https://www.facebook.com/StockInvestingPro/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-emerald-400 hover:scale-110 transition-all duration-200 flex items-center justify-center p-1.5 rounded-full border border-zinc-800 hover:border-emerald-500/30 bg-zinc-950/40"
              aria-label="Facebook"
            >
              <Facebook size={16} />
            </a>
            <a
              href="https://www.instagram.com/stockinvestingpro/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-emerald-400 hover:scale-110 transition-all duration-200 flex items-center justify-center p-1.5 rounded-full border border-zinc-800 hover:border-emerald-500/30 bg-zinc-950/40"
              aria-label="Instagram"
            >
              <Instagram size={16} />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.stockinvestingpro.app"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-105 transition-transform duration-200 block"
              aria-label={t("footer.get_it_on_google_play")}
            >
              <img
                src="/Google_Play_Store_badge_EN.webp"
                alt={t("footer.get_it_on_google_play")}
                width={135}
                height={40}
                className="h-10 w-auto aspect-[135/40] object-contain"
                loading="lazy"
              />
            </a>
            <a
              href="https://apps.apple.com/au/app/stock-investing-pro/id6789043150"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:scale-105 transition-transform duration-200 block"
              aria-label={t("footer.get_it_on_app_store")}
            >
              <img
                src="/App_Store_Badge.svg"
                alt={t("footer.get_it_on_app_store")}
                width={135}
                height={40}
                className="h-10 w-auto aspect-[135/40] object-contain"
                loading="lazy"
              />
            </a>
            <span className="flex items-center gap-1.5 italic text-[9px] uppercase tracking-widest text-zinc-400">
              {t("footer.institutional_feed")}
            </span>
          </div>
        </footer>
      </div>
      {ModalComponents}
    </div>
  );
}


